/**
 * The record-sheet stack, addressed by the URL.
 *
 * - `?open=cust:4b1c…,tx:9f2a…` is the stack (at most 4 deep), `stab` the top
 *   sheet's tab, `sw` its width (`standard` | `wide`). A shared link reopens the
 *   exact stack over the same list; the list keeps its page, filters and scroll.
 * - Opening from a list replaces the stack; opening from inside a sheet pushes.
 * - Closing returns focus to the element the sheet was opened from, else the
 *   element marked `data-sheet-origin="<type>:<id>"`, else the main region.
 *
 * Whatever renders a sheet body wraps it in `<SheetScopeContext value={ref}>`,
 * so a link to another record inside it pushes instead of replacing.
 */
import { useRouter, useSearch } from '@tanstack/react-router';
import { createContext, useContext } from 'react';
import { SHEET_REGISTRY } from '@/components/sheet/registry';
import {
  MAX_SHEET_DEPTH,
  parseSheetStack,
  type SheetRef,
  type SheetWidth,
  serializeSheetStack,
  sheetKey,
} from '@/lib/sheet-url';
import { readStorage, writeStorage } from '@/lib/storage';

export type { SheetRef, SheetType, SheetWidth } from '@/lib/sheet-url';

interface OpenSheetOptions {
  /** The row (or its primary control) the sheet was opened from; focus returns to it. */
  fromRowEl?: HTMLElement;
}

export interface SheetActions {
  /** Replaces the whole stack with `ref` (opening from a list or the palette). */
  open(ref: SheetRef, opts?: OpenSheetOptions): void;
  /** Pushes `ref` on top (opening a record from inside a sheet). Pops back if it is already in the stack. */
  push(ref: SheetRef): void;
  /** Pops the top sheet; with a stack of one, closes. */
  pop(): void;
  /** Pops back so `index` is the top (breadcrumb crumbs). */
  popTo(index: number): void;
  /** Closes every sheet and returns focus to the originating row. */
  close(): void;
  setWidth(w: SheetWidth): void;
  setTab(tab: string | undefined): void;
}

export interface SheetApi extends SheetActions {
  stack: SheetRef[];
  /** The top sheet, or undefined when none is open. */
  top: SheetRef | undefined;
  /** The open sheet's width: the URL, else the viewer's last choice for the type, else the type's default. */
  width: SheetWidth;
  /** The open sheet's tab (`stab`). */
  tab: string | undefined;
  /** True when `ref` is anywhere in the stack. */
  isOpen(ref: SheetRef): boolean;
}

function sameRef(a: SheetRef, b: SheetRef): boolean {
  return a.type === b.type && a.id === b.id;
}

let cachedParam: string | undefined;
let cachedStack: SheetRef[] = [];

/** Parsed stack for an `open` value, cached so the array keeps its identity. */
function stackFor(param: string | undefined): SheetRef[] {
  if (param !== cachedParam) {
    cachedParam = param;
    cachedStack = parseSheetStack(param);
  }
  return cachedStack;
}

const widthKey = (type: SheetRef['type']) => `sheet:${type}:sw`;

function preferredWidth(type: SheetRef['type']): SheetWidth {
  const stored = readStorage(widthKey(type));
  return stored === 'standard' || stored === 'wide'
    ? stored
    : SHEET_REGISTRY[type].defaultWidth;
}

function focusedElement(): HTMLElement | null {
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body
    ? active
    : null;
}

const origins = new Map<string, HTMLElement>();

function returnFocus(key: string): void {
  const stored = origins.get(key);
  origins.delete(key);
  // Two frames: the router commits, then the sheet unmounts.
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const target =
        stored?.isConnected === true
          ? stored
          : (document.querySelector<HTMLElement>(
              `[data-sheet-origin="${CSS.escape(key)}"]`,
            ) ?? document.getElementById('main'));
      target?.focus({ preventScroll: false });
    }),
  );
}

/** The sheet a subtree renders in; a record link inside it pushes. */
export const SheetScopeContext = createContext<SheetRef | null>(null);

export function useSheetScope(): SheetRef | null {
  return useContext(SheetScopeContext);
}

interface SheetSearch {
  open?: string;
  stab?: string;
  sw?: SheetWidth;
}

function readOpen(search: unknown): string | undefined {
  const value = (search as { open?: unknown }).open;
  return typeof value === 'string' && value ? value : undefined;
}

/**
 * The sheet actions without subscribing to the URL: for components that only
 * open records (rows, links, palette results). Every action reads the current
 * stack when it runs, so it is never stale.
 */
export function useSheetActions(): SheetActions {
  const router = useRouter();

  function current(): SheetRef[] {
    return stackFor(readOpen(router.state.location.search));
  }

  function go(update: SheetSearch, replace = false): Promise<void> {
    return router.navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, ...update }),
      replace,
      resetScroll: false,
    } as Parameters<typeof router.navigate>[0]);
  }

  function setStack(next: readonly SheetRef[], replace = false) {
    return go(
      { open: serializeSheetStack(next), stab: undefined, sw: undefined },
      replace,
    );
  }

  function open(ref: SheetRef, opts?: OpenSheetOptions) {
    origins.clear();
    const origin = opts?.fromRowEl ?? focusedElement();
    if (origin) origins.set(sheetKey(ref), origin);
    const stack = current();
    if (stack.length === 1 && stack[0] && sameRef(stack[0], ref)) return;
    void setStack([ref]);
  }

  function push(ref: SheetRef) {
    const stack = current();
    if (stack.length === 0) {
      open(ref);
      return;
    }
    const at = stack.findIndex((item) => sameRef(item, ref));
    if (at >= 0) {
      if (at < stack.length - 1) void setStack(stack.slice(0, at + 1));
      return;
    }
    const origin = focusedElement();
    if (origin) origins.set(sheetKey(ref), origin);
    void setStack([...stack, ref].slice(-MAX_SHEET_DEPTH));
  }

  function close() {
    const base = current()[0];
    if (!base) return;
    void setStack([]);
    returnFocus(sheetKey(base));
  }

  function pop() {
    const stack = current();
    const top = stack.at(-1);
    if (!top) return;
    if (stack.length === 1) {
      close();
      return;
    }
    void setStack(stack.slice(0, -1));
    returnFocus(sheetKey(top));
  }

  function popTo(index: number) {
    const stack = current();
    if (index < 0 || index >= stack.length - 1) return;
    const above = stack[index + 1];
    void setStack(stack.slice(0, index + 1));
    if (above) returnFocus(sheetKey(above));
  }

  function setWidth(w: SheetWidth) {
    const top = current().at(-1);
    if (!top) return;
    writeStorage(widthKey(top.type), w);
    void go({ sw: w }, true);
  }

  function setTab(tab: string | undefined) {
    if (current().length === 0) return;
    void go({ stab: tab }, true);
  }

  return { open, push, pop, popTo, close, setWidth, setTab };
}

/**
 * Opens a record from a table row: its sheet when the type has a body
 * (a push inside a sheet), otherwise its full page.
 */
export function useOpenRecord(): (ref: SheetRef, rowEl?: HTMLElement) => void {
  const sheet = useSheetActions();
  const router = useRouter();
  const inSheet = useSheetScope() !== null;
  return (ref, rowEl) => {
    const entry = SHEET_REGISTRY[ref.type];
    const page = entry.fullPage?.(ref.id);
    if (!entry.load && page) {
      void router.navigate({ to: page } as Parameters<
        typeof router.navigate
      >[0]);
      return;
    }
    if (inSheet) sheet.push(ref);
    else sheet.open(ref, { fromRowEl: rowEl });
  };
}

/**
 * The sheet stack and its actions. Subscribes to `open`, `stab` and `sw`, so
 * the caller re-renders when the stack changes. Components that only open
 * records should use `useSheetActions()`.
 */
export function useSheet(): SheetApi {
  const actions = useSheetActions();
  const openParam = useSearch({
    strict: false,
    select: (search) => readOpen(search),
  });
  const tab = useSearch({
    strict: false,
    select: (search) => {
      const value = (search as { stab?: unknown }).stab;
      return typeof value === 'string' && value ? value : undefined;
    },
  });
  const swParam = useSearch({
    strict: false,
    select: (search) => {
      const value = (search as { sw?: unknown }).sw;
      return value === 'standard' || value === 'wide' ? value : undefined;
    },
  });

  const stack = stackFor(openParam);
  const top = stack.at(-1);
  const width: SheetWidth =
    swParam ?? (top ? preferredWidth(top.type) : 'standard');

  return {
    ...actions,
    stack,
    top,
    width,
    tab,
    isOpen: (ref) => stack.some((item) => sameRef(item, ref)),
  };
}
